import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="month-plan-grid"
export default class extends Controller {
  static values = {
    updateUrl: String
  }

  connect() {
    this.boundHandleWindowResize = this.handleViewportChange.bind(this)
    this.boundHandleDocumentPointerDown = this.handleDocumentPointerDown.bind(this)
    this.boundHandleScroll = this.repositionOpenEditors.bind(this)
    this.scrollContainer = this.element.querySelector("[data-month-plan-grid-scroll-container]")

    setTimeout(() => {
      this.updateStickyOffset()
    }, 100);
    this.initializeTooltipsWithin(this.element)
    window.addEventListener("resize", this.boundHandleWindowResize)
    document.addEventListener("pointerdown", this.boundHandleDocumentPointerDown)
    this.scrollContainer?.addEventListener("scroll", this.boundHandleScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("resize", this.boundHandleWindowResize)
    document.removeEventListener("pointerdown", this.boundHandleDocumentPointerDown)
    this.scrollContainer?.removeEventListener("scroll", this.boundHandleScroll)

    this.disposeTooltipsWithin(this.element)
  }

  open(event) {
    const cellContainer = event.currentTarget.closest("[data-month-plan-grid-cell-container]")
    if (!cellContainer) return

    this.hideTooltip(event.currentTarget)

    this.closeAllEditors(cellContainer)

    const editor = cellContainer.querySelector("[data-month-plan-grid-editor]")
    if (!editor) return

    editor.classList.remove("d-none")
    cellContainer.classList.add("is-editing")
    this.positionEditor(cellContainer)

    const textarea = editor.querySelector("textarea")
    if (textarea) textarea.focus()
  }

  async chooseStatus(event) {
    const cellContainer = event.currentTarget.closest("[data-month-plan-grid-cell-container]")
    if (!cellContainer) return

    const status = event.currentTarget.dataset.status || ""
    const statusInput = cellContainer.querySelector("[data-month-plan-grid-status-input]")
    const commentField = cellContainer.querySelector("textarea")

    if (statusInput) statusInput.value = status

    this.updateStatusButtons(cellContainer, status)

    await this.submitCell(cellContainer, {
      status,
      comment: commentField?.value.trim() || ""
    }, { keepOpen: true })
  }

  cancel(event) {
    const cellContainer = event.currentTarget.closest("[data-month-plan-grid-cell-container]")
    if (!cellContainer) return

    this.hideEditor(cellContainer)
  }

  async save(event) {
    const cellContainer = event.currentTarget.closest("[data-month-plan-grid-cell-container]")
    if (!cellContainer) return

    const editor = cellContainer.querySelector("[data-month-plan-grid-editor]")
    const statusInput = cellContainer.querySelector("[data-month-plan-grid-status-input]")
    const commentField = cellContainer.querySelector("textarea")
    const status = (editor?.dataset.savedStatus ?? statusInput?.defaultValue ?? statusInput?.value) || ""
    const comment = commentField?.value.trim() || ""

    await this.submitCell(cellContainer, { status, comment })
  }

  async clear(event) {
    const cellContainer = event.currentTarget.closest("[data-month-plan-grid-cell-container]")
    if (!cellContainer) return

    await this.submitCell(cellContainer, { status: "", comment: "" })
  }

  async submitCell(cellContainer, payload, { keepOpen = false } = {}) {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const userId = cellContainer.dataset.userId
    const workDate = cellContainer.dataset.workDate

    cellContainer.classList.add("is-saving")

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          user_id: userId,
          work_date: workDate,
          status: payload.status,
          comment: payload.comment
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Не удалось сохранить изменение")
      }

      this.disposeTooltipsWithin(cellContainer)
      cellContainer.innerHTML = data.cell_html
      this.updateRowStats(userId, data.row_stats)
      this.initializeTooltipsWithin(cellContainer)

      if (keepOpen) {
        const newEditor = cellContainer.querySelector("[data-month-plan-grid-editor]")
        if (newEditor) {
          newEditor.classList.remove("d-none")
          cellContainer.classList.add("is-editing")
          this.positionEditor(cellContainer)
          const textarea = newEditor.querySelector("textarea")
          if (textarea) textarea.focus()
        }
      } else {
        this.hideEditor(cellContainer)
      }
    } catch (error) {
      window.alert(error.message)
    } finally {
      cellContainer.classList.remove("is-saving")
    }
  }

  updateRowStats(userId, rowStats) {
    if (!rowStats) return

    Object.entries(rowStats).forEach(([key, value]) => {
      const statCell = this.element.querySelector(`[data-month-plan-grid-stat="${key}"][data-user-id="${userId}"]`)
      if (statCell) statCell.textContent = value
    })
  }

  handleDocumentPointerDown(event) {
    if (event.target.closest("[data-month-plan-grid-cell-container]")) return

    this.closeAllEditors()
  }

  closeAllEditors(exceptContainer = null) {
    this.element.querySelectorAll("[data-month-plan-grid-cell-container].is-editing").forEach((cellContainer) => {
      if (cellContainer !== exceptContainer) {
        this.hideEditor(cellContainer)
      }
    })
  }

  hideEditor(cellContainer) {
    this.restoreEditorState(cellContainer)

    const editor = cellContainer.querySelector("[data-month-plan-grid-editor]")
    if (editor) {
      editor.classList.add("d-none")
      editor.classList.remove("is-above")
      editor.style.left = ""
      editor.style.top = ""
      editor.style.visibility = ""
    }

    cellContainer.classList.remove("is-editing")
  }

  restoreEditorState(cellContainer) {
    const editor = cellContainer.querySelector("[data-month-plan-grid-editor]")
    const statusInput = cellContainer.querySelector("[data-month-plan-grid-status-input]")
    const commentField = cellContainer.querySelector("textarea")
    const savedStatus = editor?.dataset.savedStatus ?? statusInput?.defaultValue ?? ""
    const savedComment = editor?.dataset.savedComment ?? commentField?.defaultValue ?? ""

    if (statusInput) {
      statusInput.value = savedStatus
      this.updateStatusButtons(cellContainer, savedStatus)
    }

    if (commentField) {
      commentField.value = savedComment
    }
  }

  updateStatusButtons(cellContainer, activeStatus) {
    cellContainer.querySelectorAll("[data-month-plan-grid-status-option]").forEach((button) => {
      const isActive = button.dataset.status === activeStatus
      button.classList.toggle("is-active", isActive)
      button.setAttribute("aria-pressed", isActive ? "true" : "false")
    })
  }

  repositionOpenEditors() {
    this.element.querySelectorAll("[data-month-plan-grid-cell-container].is-editing").forEach((cellContainer) => {
      this.positionEditor(cellContainer)
    })
  }

  handleViewportChange() {
    this.updateStickyOffset()
    this.repositionOpenEditors()
  }

  positionEditor(cellContainer) {
    const editor = cellContainer.querySelector("[data-month-plan-grid-editor]")
    const summaryButton = cellContainer.querySelector(".month-plan-cell__summary")
    if (!editor || !summaryButton) return

    editor.style.visibility = "hidden"
    editor.style.left = "0px"
    editor.style.top = "0px"

    const bounds = this.visibleBoundsFor(cellContainer)
    const summaryRect = summaryButton.getBoundingClientRect()
    const editorRect = editor.getBoundingClientRect()
    const editorWidth = editorRect.width || editor.offsetWidth
    const editorHeight = editorRect.height || editor.offsetHeight
    const gap = 8

    // Viewport-relative coordinates (position: fixed)
    const preferredLeft = summaryRect.left + (summaryRect.width / 2) - (editorWidth / 2)
    const left = this.clamp(preferredLeft, bounds.left, bounds.right - editorWidth)

    const belowTop = summaryRect.bottom + gap
    const aboveTop = summaryRect.top - editorHeight - gap
    const minTop = bounds.top
    const maxTop = bounds.bottom - editorHeight

    let top = belowTop
    let opensAbove = false

    if (belowTop > maxTop && aboveTop >= minTop) {
      top = aboveTop
      opensAbove = true
    } else {
      top = this.clamp(belowTop, minTop, maxTop)
      opensAbove = top < belowTop && aboveTop >= minTop
    }

    editor.style.left = `${Math.round(left)}px`
    editor.style.top = `${Math.round(top)}px`
    editor.style.visibility = ""
    editor.classList.toggle("is-above", opensAbove)
  }

  visibleBoundsFor(cellContainer) {
    const viewportPadding = 8
    const stickyTopOffset = this.headerBottomOffset()
    const viewportBounds = {
      left: viewportPadding,
      top: Math.max(viewportPadding, stickyTopOffset + viewportPadding),
      right: window.innerWidth - viewportPadding,
      bottom: window.innerHeight - viewportPadding
    }

    const scrollContainer = cellContainer.closest("[data-month-plan-grid-scroll-container]")
    if (!scrollContainer) return viewportBounds

    const scrollRect = scrollContainer.getBoundingClientRect()

    return {
      left: Math.max(viewportBounds.left, scrollRect.left + 4),
      top: Math.max(viewportBounds.top, scrollRect.top + 4),
      right: Math.min(viewportBounds.right, scrollRect.right - 4),
      bottom: Math.min(viewportBounds.bottom, scrollRect.bottom - 4)
    }
  }

  clamp(value, min, max) {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return value
    if (max < min) return min

    return Math.min(Math.max(value, min), max)
  }

  updateStickyOffset() {
    if (!this.scrollContainer) return

    const available = window.innerHeight - $(".pc-header").height() - 10;
    
    this.scrollContainer.style.maxHeight = `${Math.max(300, available)}px`
  }

  headerBottomOffset() {
    const header = document.querySelector("header.pc-header")
    if (!header) return 0

    return Math.max(0, Math.round(header.getBoundingClientRect().bottom))
  }

  initializeTooltipsWithin(rootElement) {
    if (!window.bootstrap?.Tooltip) return

    rootElement.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      window.bootstrap.Tooltip.getOrCreateInstance(element, {
        container: "body",
        boundary: document.body
      })
    })
  }

  disposeTooltipsWithin(rootElement) {
    if (!window.bootstrap?.Tooltip) return

    rootElement.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      const instance = window.bootstrap.Tooltip.getInstance(element)
      if (!instance) return

      instance.hide()
      instance.dispose()
    })
  }

  hideTooltip(element) {
    if (!window.bootstrap?.Tooltip) return

    const instance = window.bootstrap.Tooltip.getInstance(element)
    instance?.hide()
  }
}
